import { useEffect, useRef, useState } from 'react';

export type ExperienceMode = 'full' | 'lite' | 'reduced';

const root = document.documentElement;
let currentMode: ExperienceMode = 'full';

const lowerPerformanceDevice = (): boolean => {
  const navigatorWithMemory = navigator as Navigator & { deviceMemory?: number };
  return (navigator.hardwareConcurrency > 0 && navigator.hardwareConcurrency <= 4)
    || (navigatorWithMemory.deviceMemory !== undefined && navigatorWithMemory.deviceMemory <= 4);
};

const resolveMode = (reducedMotion: boolean): ExperienceMode => {
  if (reducedMotion) return 'reduced';
  return lowerPerformanceDevice() ? 'lite' : 'full';
};

const applyMode = (mode: ExperienceMode): void => {
  currentMode = mode;
  root.dataset.experienceMode = mode;
};

export const configureExperience = (): (() => void) => {
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  const sync = (): void => applyMode(resolveMode(media.matches));
  sync();
  media.addEventListener?.('change', sync);
  return () => media.removeEventListener?.('change', sync);
};

export const experienceMode = (): ExperienceMode => currentMode;

/**
 * Marks a moment as fresh only when its key changes after the component has mounted.
 * Restored/reconnected snapshots therefore render the truth without replaying old emotion.
 */
export const useFreshMoment = (key: string | null, onFresh?: () => void): boolean => {
  const mounted = useRef(false);
  const previous = useRef<string | null>(key);
  const callback = useRef(onFresh);
  const [fresh, setFresh] = useState(false);
  callback.current = onFresh;

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      previous.current = key;
      return;
    }
    if (!key || key === previous.current) return;
    previous.current = key;
    if (document.visibilityState !== 'visible') return;
    callback.current?.();
    setFresh(true);
    const timer = window.setTimeout(() => setFresh(false), 1_200);
    return () => window.clearTimeout(timer);
  }, [key]);

  return fresh;
};

